import { useEffect, useRef } from 'react'
import type { Intent } from './simulation'
import { clearIntent, freshIntent } from './inputState'

const p1Map:Record<string,keyof Intent>={KeyA:'left',KeyD:'right',KeyS:'block',KeyJ:'light',KeyK:'heavy',KeyL:'special',KeyU:'throw',ShiftLeft:'dash',KeyQ:'quantum',KeyW:'jump',Space:'superJump',KeyF:'flight',KeyR:'boost',KeyC:'dive'}
const p2Map:Record<string,keyof Intent>={ArrowLeft:'left',ArrowRight:'right',ArrowDown:'block',ArrowUp:'jump',Numpad1:'light',Digit1:'light',Numpad2:'heavy',Digit2:'heavy',Numpad3:'special',Digit3:'special',Numpad4:'throw',Digit4:'throw',Numpad0:'dash',Numpad8:'depthUp',Numpad5:'depthDown',Numpad7:'quantum',Digit7:'quantum',NumpadDecimal:'superJump',Numpad6:'flight',Numpad9:'boost',NumpadSubtract:'dive'}
const LATCHED_KEYS=new Set<keyof Intent>(['light','heavy','special','throw','quantum','jump','superJump','flight']),DEADZONE=.28

function applyGamepad(target:Intent,keyboard:Intent,pad?:Gamepad|null){
 if(!pad){Object.assign(target,keyboard);return}
 const axis=pad.axes[0]??0,vertical=pad.axes[1]??0,depthModifier=!!pad.buttons[5]?.pressed,left=axis<-DEADZONE||!!pad.buttons[14]?.pressed,right=axis>DEADZONE||!!pad.buttons[15]?.pressed
 target.left=keyboard.left||(!depthModifier&&left)
 target.right=keyboard.right||(!depthModifier&&right)
 target.depthUp=keyboard.depthUp||(depthModifier&&vertical<-DEADZONE)
 target.depthDown=keyboard.depthDown||(depthModifier&&vertical>DEADZONE)
 target.block=keyboard.block||!!pad.buttons[6]?.pressed||!!pad.buttons[13]?.pressed
 target.light=keyboard.light||!!pad.buttons[0]?.pressed
 target.heavy=keyboard.heavy||!!pad.buttons[2]?.pressed
 target.special=keyboard.special||!!pad.buttons[3]?.pressed
 target.throw=keyboard.throw||!!pad.buttons[4]?.pressed
 target.dash=keyboard.dash||!!pad.buttons[1]?.pressed||!!pad.buttons[5]?.pressed
 target.quantum=keyboard.quantum||!!pad.buttons[8]?.pressed
 target.jump=keyboard.jump||!!pad.buttons[12]?.pressed
 target.superJump=keyboard.superJump||!!pad.buttons[10]?.pressed
 target.flight=keyboard.flight||!!pad.buttons[7]?.pressed
 target.boost=keyboard.boost||!!pad.buttons[11]?.pressed
 target.dive=keyboard.dive||!!pad.buttons[9]?.pressed
}

function consume(current:Intent,latched:Intent){
 const next={...current,light:latched.light,heavy:latched.heavy,special:latched.special,throw:latched.throw,quantum:latched.quantum,jump:latched.jump,superJump:latched.superJump,flight:latched.flight}
 latched.light=false
 latched.heavy=false
 latched.special=false
 latched.throw=false
 latched.quantum=false
 latched.jump=false
 latched.superJump=false
 latched.flight=false
 return next
}

export function useControls(){
 const player=useRef<Intent>(freshIntent()),enemy=useRef<Intent>(freshIntent()),keyboardPlayer=useRef<Intent>(freshIntent()),keyboardEnemy=useRef<Intent>(freshIntent()),latchedPlayer=useRef<Intent>(freshIntent()),latchedEnemy=useRef<Intent>(freshIntent()),prevPadButtons=useRef([Array(8).fill(false),Array(8).fill(false)] as boolean[][])
 useEffect(()=>{
  const clearTransientInput=()=>{clearIntent(keyboardPlayer.current);clearIntent(keyboardEnemy.current);clearIntent(latchedPlayer.current);clearIntent(latchedEnemy.current);clearIntent(player.current);clearIntent(enemy.current);prevPadButtons.current=[Array(8).fill(false),Array(8).fill(false)]}
  const setKey=(event:KeyboardEvent,down:boolean)=>{
   const depthArrow=event.code==='ArrowUp'||event.code==='ArrowDown',depthP1=depthArrow&&(event.shiftKey||keyboardPlayer.current.dash)
   if(depthArrow&&!down){keyboardPlayer.current[event.code==='ArrowUp'?'depthUp':'depthDown']=false}
   if(depthP1){keyboardPlayer.current[event.code==='ArrowUp'?'depthUp':'depthDown']=down;event.preventDefault();return}
   const p1=p1Map[event.code],p2=p2Map[event.code]
   if(p1){keyboardPlayer.current[p1]=down;if(down&&!event.repeat&&LATCHED_KEYS.has(p1))latchedPlayer.current[p1]=true;event.preventDefault()}
   if(p2){keyboardEnemy.current[p2]=down;if(down&&!event.repeat&&LATCHED_KEYS.has(p2))latchedEnemy.current[p2]=true;event.preventDefault()}
  }
  const down=(e:KeyboardEvent)=>setKey(e,true),up=(e:KeyboardEvent)=>setKey(e,false),visibility=()=>{if(document.visibilityState!=='visible')clearTransientInput()}
  let frame=0
  const poll=()=>{
   const pads=navigator.getGamepads?.()??[]
   ;[0,1].forEach(index=>{
    const pad=pads[index],target=index===0?player.current:enemy.current,keyboard=index===0?keyboardPlayer.current:keyboardEnemy.current,latched=index===0?latchedPlayer.current:latchedEnemy.current
    if(!pad)prevPadButtons.current[index]=Array(8).fill(false)
    applyGamepad(target,keyboard,pad)
    const next=[!!pad?.buttons[0]?.pressed,!!pad?.buttons[2]?.pressed,!!pad?.buttons[3]?.pressed,!!pad?.buttons[4]?.pressed,!!pad?.buttons[8]?.pressed,!!pad?.buttons[12]?.pressed,!!pad?.buttons[10]?.pressed,!!pad?.buttons[7]?.pressed]
    const prev=prevPadButtons.current[index]
    if(next[0]&&!prev[0])latched.light=true
    if(next[1]&&!prev[1])latched.heavy=true
    if(next[2]&&!prev[2])latched.special=true
    if(next[3]&&!prev[3])latched.throw=true
    if(next[4]&&!prev[4])latched.quantum=true
    if(next[5]&&!prev[5])latched.jump=true
    if(next[6]&&!prev[6])latched.superJump=true
    if(next[7]&&!prev[7])latched.flight=true
    prevPadButtons.current[index]=next
   })
   frame=requestAnimationFrame(poll)
  }
  window.addEventListener('keydown',down,{passive:false});window.addEventListener('keyup',up,{passive:false});window.addEventListener('blur',clearTransientInput);document.addEventListener('visibilitychange',visibility);frame=requestAnimationFrame(poll)
  return()=>{cancelAnimationFrame(frame);window.removeEventListener('keydown',down);window.removeEventListener('keyup',up);window.removeEventListener('blur',clearTransientInput);document.removeEventListener('visibilitychange',visibility)}
 },[])
 return{player,enemy,consumePlayer:()=>consume(player.current,latchedPlayer.current),consumeEnemy:()=>consume(enemy.current,latchedEnemy.current)}
}
